/**
 * Schema-management import + FM-links flows.
 *
 * `checkForDuplicateClient` builds the existingClients set from the current
 * config + scheduled changes (used by SYF card-access + Athos flows).
 * `getImportPreview` computes and displays a preview from another CTID /
 * release-version's config.
 * `handleImportConfigRequest` commits the imported summary changes into the
 * client config.
 * `addNewClient` inserts a new client key with schema-default template.
 * `updateFMLinks` reconciles pending File Manager links.
 */

import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import set from "lodash/set";
import { getStore } from "@store";
import schemaManagementAdapter from "@features/schema-management/adapters";
import commercialFmsAdapter from "@features/files/adapters/commercial-files";
import { getJWT } from "@utils/browser-helpers";
import { arrayUnion } from "@utils/array-helpers";
import { isEmptyObject, stringifyNestedKeyPaths } from "@utils/object-helpers";
import {
  logDiffs,
} from "@features/schema-management/utils/schemaManagementHelpers";
import { CONFIG_SCHEDULER } from "@actions/types";
import { STATE_CONTANTS } from "@constants";
import {
  DISPATCH_ERRORS,
  REWARDS_MFE_ATHOS,
  STATEMENTS_MFE_ATHOS,
  SYF_CARD_ACCESS,
  SYPI_BRC,
} from "@constants/schema-management";
import {
  showSpinner,
  stopSpinner,
  setAlertDock,
  closeDock,
  enableSave,
  clickTab,
  toggleModal,
  recordChangeObject,
  logChangedKey,
} from "@actions/global";
import {
  setExistingClients,
  setClientVisibility,
  setNewClients,
  setDeletedClients,
  loadImportedInputs,
  loadConfig,
  loadSchemaSectionPreview,
  setSchemaSectionSummaryName,
  toggleSummaryModal,
  clearFMLinks,
} from "@features/schema-management/actions";
import { getSummaryPreview, modifySchema } from "./sm-shared";
import { fetchSMConfig } from "./sm-fetch";
import { setGeneralPreviewSummary } from "./sm-summary";
import { modifyConfig } from "./sm-shared";

export const checkForDuplicateClient = async (
  config = null,
  feature,
  store = getStore()
) => {
  let formData = config ? config : store.getState().schemaAllView.initialConfig;
  const { featureName } = store.getState().schemaAllView;
  const { scheduleDraftSet } = store.getState().schemaConfigScheduler;
  const { activeReleaseVersion } = store.getState().schema;
  const isScheduleDraftSet = scheduleDraftSet && scheduleDraftSet.length;
  const isCommercial = featureName === SYPI_BRC;
  const { apiError, configSchedules } = isScheduleDraftSet
    ? { configSchedules: scheduleDraftSet }
    : await schemaManagementAdapter.fetchScheduledDraftList({}, isCommercial);
  if (!apiError && configSchedules) {
    let scheduledClients = new Set(
      configSchedules
        .filter(
          (schedule) =>
            schedule.status === STATE_CONTANTS.SCHEDULED_STATUS &&
            schedule.release_version === activeReleaseVersion
        )
        .flatMap((schedule) =>
          schedule.sectionsModified?.map((section) => section.toLowerCase())
        )
    );
    let configClients =
      feature === SYF_CARD_ACCESS
        ? formData
            .filter((config) => !!config.client)
            .map((config) => config.client?.toLowerCase())
        : Object.getOwnPropertyNames(formData);
    let existingClients = arrayUnion([...scheduledClients], configClients);
    if (feature === STATEMENTS_MFE_ATHOS || feature === REWARDS_MFE_ATHOS) {
      let athosClients = existingClients.reduce((acc, client) => {
        acc[client] = false;
        return acc;
      }, {});
      store.dispatch(setClientVisibility(athosClients));
    }
    store.dispatch(setExistingClients(existingClients));
  }
};

/** import */
export const getImportPreview = async (
  importedCTID,
  importedReleaseVersion,
  section,
  store = getStore()
) => {
  store.dispatch(showSpinner());
  store.dispatch(
    clickTab(STATE_CONTANTS.DISPLAY_IMPORT_SUMMARY, CONFIG_SCHEDULER)
  );
  const { initialConfig, schemaType, featureName } =
    store.getState().schemaAllView;
  const { activeReleaseVersion } = store.getState().schema;
  const isSchemaArrayTyped = schemaType === "array";
  const importedConfig = await fetchSMConfig(
    importedReleaseVersion,
    importedCTID
  );
  let updatedImportedConfig = cloneDeep(initialConfig);
  if (importedConfig?.sm_config && !isEmptyObject(importedConfig?.sm_config)) {
    updatedImportedConfig =
      section === "entire"
        ? importedConfig?.sm_config
        : featureName === SYF_CARD_ACCESS
          ? [
              ...updatedImportedConfig,
              ...importedConfig?.sm_config.filter(
                (clientConfig) =>
                  clientConfig.client.toLowerCase() === section.toLowerCase()
              ),
            ]
          : {
              ...updatedImportedConfig,
              [section]: importedConfig?.sm_config?.[section],
            };
    updatedImportedConfig = isSchemaArrayTyped
      ? updatedImportedConfig.reduce((acc, item) => {
          item.client = item.client.toLowerCase();
          acc = { ...acc, [item.client]: item };
          return acc;
        }, {})
      : updatedImportedConfig;
    let updatedInitialConfig = cloneDeep(initialConfig);
    updatedInitialConfig = isSchemaArrayTyped
      ? updatedInitialConfig.reduce((acc, item) => {
          item.client = item.client.toLowerCase();
          acc = { ...acc, [item.client]: item };
          return acc;
        }, {})
      : updatedInitialConfig;
    let changeDict = logDiffs(
      updatedInitialConfig,
      updatedImportedConfig,
      featureName === REWARDS_MFE_ATHOS ? featureName : ""
    );
    let loggedDiffs = Object.keys(changeDict);
    const { newConfig, summaryPreview, successFailedImportedKeysReport } =
      await getSummaryPreview(
        isSchemaArrayTyped,
        updatedInitialConfig,
        loggedDiffs,
        updatedImportedConfig,
        section
      );
    await setGeneralPreviewSummary(
      "",
      summaryPreview,
      updatedInitialConfig,
      successFailedImportedKeysReport
    );
    const ctid = `${getJWT().ctid}`;
    updatedImportedConfig =
      section === "entire"
        ? newConfig
        : { ...updatedImportedConfig, [section]: newConfig };
    store.dispatch(
      loadSchemaSectionPreview(
        updatedImportedConfig,
        section,
        ctid,
        activeReleaseVersion,
        importedCTID,
        importedReleaseVersion,
        "preview"
      )
    );
    store.dispatch(
      setSchemaSectionSummaryName(STATE_CONTANTS.DISPLAY_IMPORT_SUMMARY)
    );
    store.dispatch(toggleSummaryModal(true));
  } else {
    const errorDetail =
      typeof importedConfig === "string" ? ` ${importedConfig}` : "";
    store.dispatch(
      setAlertDock({
        title: "Config Import Failed",
        message: `No config file found for CTID ${importedCTID} release version ${importedReleaseVersion}.${errorDetail} The config may not have been created yet for this release version.`,
      })
    );
  }
  store.dispatch(stopSpinner());
};

export const handleImportConfigRequest = (store = getStore()) => {
  store.dispatch(stopSpinner());
  const { clientSchema } = store.getState().schema;
  const { summaryView, schemaSection } =
    store.getState().schemaConfigScheduler.generalPreview;
  const {
    schemaManagementConfig,
    initialConfig,
    schemaType,
    deletedClients,
    featureName,
    isAthosConfig,
  } = store.getState().schemaAllView;
  const isSchemaArrayTyped = schemaType === "array";
  if (summaryView.length > 0) {
    let updatedImports = {};
    let mergedConfig = cloneDeep(schemaManagementConfig);
    if (isSchemaArrayTyped) {
      const configChanges = summaryView.reduce((acc, item) => {
        set(acc, item.keyName, item.newKeyValue);
        return acc;
      }, {});
      const paths = stringifyNestedKeyPaths(configChanges);
      paths.forEach((path) => {
        let client = path.split(".")[0];
        const indexOfChange = mergedConfig.findIndex(
          (item) => item.client.toLowerCase() === client.toLowerCase()
        );
        if (indexOfChange === -1) {
          const newClientKey = `root.${mergedConfig.length}.client`;
          store.dispatch(setNewClients(newClientKey, client));
          mergedConfig.push(configChanges[client]);
        } else {
          const indexedPath = path.replace(client, indexOfChange);
          set(mergedConfig, indexedPath, get(configChanges, path));
          if (deletedClients.hasOwnProperty(client)) {
            const updatedDeletedClients = cloneDeep(deletedClients);
            delete updatedDeletedClients[client];
            store.dispatch(setDeletedClients(updatedDeletedClients));
          }
        }
        updatedImports = {
          ...updatedImports,
          [path]: get(configChanges, path),
        };
      });
    } else if (isAthosConfig) {
      let newClients = [];
      summaryView.forEach((key) => {
        let client = key.keyName.split(".")[0];
        if (!mergedConfig.hasOwnProperty(client)) {
          newClients.push(client);
          store.dispatch(setNewClients(client, client));
        }
        set(mergedConfig, key.keyName, key.newKeyValue);
        updatedImports = {
          ...updatedImports,
          [key.keyName]: key.newKeyValue,
        };
      });
      newClients.forEach((client) => modifySchema(client));
    } else {
      summaryView.forEach((key) => {
        if (!key.disable) {
          updatedImports = {
            ...updatedImports,
            [key.keyName]: key.newKeyValue,
          };
          set(mergedConfig, key.keyName, key.newKeyValue);
        }
      });
    }

    let loggedDiffs = logDiffs(initialConfig, mergedConfig, featureName);
    store.dispatch(recordChangeObject(loggedDiffs));
    store.dispatch(logChangedKey(Object.keys(loggedDiffs)));
    store.dispatch(loadImportedInputs(updatedImports));
    store.dispatch(loadConfig(mergedConfig));
    DISPATCH_ERRORS[featureName](mergedConfig, clientSchema);
    store.dispatch(
      setAlertDock({
        title: `Imported Config Confirmed`,
        message: `New configs have been applied to ${
          schemaSection === "entire" ? "config" : `${schemaSection}`
        }. Review and save your work by scheduling your changes or creating a draft.`,
      })
    );
  }
  store.dispatch(clickTab(STATE_CONTANTS.SCHEDULER_STATUS, CONFIG_SCHEDULER));
  store.dispatch(stopSpinner());
};

export const addNewClient = async (clientName, store = getStore()) => {
  store.dispatch(showSpinner());
  modifySchema(clientName);
  modifyConfig(clientName);
  store.dispatch(toggleModal(false));
  store.dispatch(stopSpinner());
};

export const updateFMLinks = async (store = getStore()) => {
  const storeContents = store.getState();
  let { schemaAllView } = storeContents;
  let { fmLink, fmLinkBucket } = schemaAllView;
  let finalReport = {};
  const values = Object.values(fmLink);

  let aErrors = [];

  // Handle pre-existing link errors
  for (let index = 0; index < values.length; index++) {
    let newFileLocation = values[index].split(fmLinkBucket)[1];
    let result =
      await commercialFmsAdapter.fetchIndividualFileLocation(newFileLocation);

    if (result.length > 0)
      aErrors.push(`${newFileLocation}: File already exists.`);
  }

  // Catch-all for any other errors
  if (aErrors.length === 0) {
    for (let index = 0; index < values.length; index++) {
      let newFileLocation = values[index].split(fmLinkBucket)[1];
      let result = await commercialFmsAdapter.postNewFileLocation(
        newFileLocation,
        {}
      );

      let { success } = result;
      if (!success) aErrors.push(`${newFileLocation}: ${result}`);
    }
  }

  if (aErrors.length > 0)
    finalReport = {
      type: "ALERT_DOCK",
      title: `There was an issue updating the File Manager links!`,
      message: `${aErrors.map((error) => `${error}\n`)}`,
    };
  else
    finalReport = {
      type: "TOAST_MESSAGE",
      message: `File Manager links were successfully updated!`,
    };

  store.dispatch(closeDock());
  store.dispatch(clearFMLinks());
  store.dispatch(enableSave());

  return finalReport;
};
